import moment from 'moment';
import defaultOption from './defaultOption';
import { leftTitleReserver, rightTitleReserver } from '../../utils/chartUtils';
import commonUtils from '../../utils/commonUtils';
import { chartColors } from './chartTheme';

export const PNGChartHeight = 400;
export const PNGChartScale = 2;

/**
 * 坐标轴名称
 * @author peng.wu
 */
export interface IAxis {
  /**
   * x轴名称
   */
  x: {
    title?: string; // 坐标轴名称
    labelsFormatter?: (value: string) => void; // 坐标轴的显示规则定义
    labelRotation?: number; // 坐标轴的倾斜角度（默认不倾斜）
    other?: any;
    hide?: boolean; // 默认false, 不隐藏
    plotDate?: number; // 阴影覆盖日期
  };

  /**
   * y轴名称
   */
  y: IYAxis[] | IYAxis;
}
export interface IYAxis {
  title?: string;
  labelsFormatter?: (value: string) => void;
  other?: any;
  hide?: boolean; // 默认false, 不隐藏
}

const DefaultAxis: IAxis = {
  x: {},
  y: {}
};

export interface IUpdateFlagConfig {
  minTime: number;
  maxTime: number;
  chartData: any;
  highChartSeries: any;
  lowChartSeries: any;
  timeString: string;
  orderHigh: string;
  orderLow: string;
}

export interface IChartConfig {
  seriesList: any[];
  downloadList?: any[];
  chartData?: any[];
  xList?: string[];
  axis?: IAxis;
  height?: number | string;
  tooltipFormatter?: (data: any) => void;
  tooltipOther?: any;
  legendEnabled?: boolean;
  colors?: string[];
  chartTitle?: string;
  showMADesc?: boolean;
  y1Direction?: string;
  y2Direction?: string;
  colorAxis?: any;
  credits?: any;
}
/**
 * 图表传值
 * @seriesList 作图数据
 * @downloadList 下载的绘图数据（新简单）
 * @chartData 下载的绘图数据（旧复杂）
 * @xList 当x轴为string类型时，x的坐标集合
 * @axis 坐标轴相关设置
 * @height 图表高度
 * @tooltipFormatter 提示框内容格式设置方法
 * @legendEnabled 图例是否显示（默认显示）
 * @colors 当前图使用的色序修改（默认为色序1）
 * @chartTitle 图的标题
 */
export function getCommonChartConfig(config: IChartConfig, type?: 'chart' | 'stock' | 'map') {
  const {
    axis = DefaultAxis,
    height,
    tooltipFormatter,
    legendEnabled = true,
    chartTitle = '',
    tooltipOther,
    seriesList,
    downloadList,
    xList,
    chartData,
    colors,
    showMADesc,
    colorAxis,
    y1Direction = 'left',
    y2Direction = 'right'
  } = config;

  if (!seriesList) {
    return {};
  }
  if (seriesList.length === 0) {
    return {
      ...defaultOption,
      title: {
        text: chartTitle
      }
    };
  }
  const series = colors ? changeColor(seriesList, colors) : seriesList;

  const { x = {}, y = {} } = axis;
  let yAxis;
  const { axis: xAxis } = formatAxis({ data: x, xList }); // , title: xAxisTile
  if (!Array.isArray(y)) {
    const { axis: y1Axis } = formatAxis({ data: y, yDirection: y1Direction });
    yAxis = y1Axis;
  } else {
    const { axis: y1Axis } = formatAxis({ data: y[0], yDirection: y1Direction }); // , title: y1AxisTile
    const { axis: y2Axis } = formatAxis({ data: y[1], yDirection: y2Direction }); // , title: y2AxisTile?
    yAxis = y2Axis ? [y1Axis, y2Axis] : [y1Axis];
    // yAxis.push({lineWidth: 1, opposite: true});
  }
  const dataGroup =
    type === 'stock'
      ? {
          dataGrouping: {
            units: [['day', [1]]]
          }
        }
      : {};
  var option = {
    ...defaultOption,
    chart: {
      height: height || 400
    },
    title: {
      text: chartTitle
    },
    colorAxis,
    xAxis: xAxis,
    yAxis: yAxis,
    tooltip: {
      formatter: function() {
        const that = this as any;
        if (tooltipFormatter) {
          const tooltip = tooltipFormatter(that);
          return tooltip;
        }
      },
      ...tooltipOther
    },
    legend: {
      enabled: legendEnabled
    },
    plotOptions: {
      series: {
        turboThreshold: 10000,
        ...dataGroup
      }
    },
    showMADesc,
    series,
  };
  return { ...option, downloadList, height, chartData, isNew: true };
}

// 对坐标轴(x、y)的数据格式构造的统一方法
function formatAxis(payload: any) {
  const { data, yDirection, xList } = payload;
  if (!data) {
    return {};
  }
  const title = data.title || '';
  const text = !yDirection ? title : yDirection === 'left' ? leftTitleReserver(title) : rightTitleReserver(title);
  const plotBands = data.plotDate ? { color: 'rgba(33,38,50, 0.6)', from: 0, to: data.plotDate } : undefined;
  const axis = {
    // tickInterval: 0.01,
    categories: xList,
    labels: {
      formatter: function() {
        const that = this as any;
        const { labelsFormatter } = data;
        const fun = labelsFormatter ? labelsFormatter(that.value) : that.value;
        return fun;
      },
      rotation: data.labelRotation
    },
    title: {
      enabled: !data.hide,
      useHTML: true,
      text: text,
      noFormatText: title
    },
    plotBands,
    ...data.other
  };
  return { axis, title };
}

function changeColor(data: any[], colors: string[]) {
  let dataList: any[] = commonUtils.deep(data);
  dataList.map((series, index) => {
    series.color = colors[index % colors.length];
  });
  return dataList;
}

/**
 * 获取chart的series (已分类-x轴为日期、数字类型)
 * @chartData 数据
 * @formartFun (item: any) => void 数据格式转换方法
 * @xKey string 代表x轴的字段名，当x轴为日期类型时，下载需要原日期使用
 */
export function chartTypeMap(data: any[], formatFun: (item: any) => any, xKey?: string) {
  if (!data || data.length === 0) {
    return { seriesList: [], downloadList: [] };
  }
  const seriesList: any = [];
  const downloadList: any = [];
  data.map((series, index) => {
    const seriesData: any[] = [];
    const downloadData: any[] = [];
    if (!series || !series.data) {
      return;
    }
    series.data.forEach(point => {
      const newPoint = formatFun(point);
      seriesData.push(newPoint);
      if (xKey) {
        downloadData.push({ ...newPoint, x: point[xKey] });
      } else {
        downloadData.push(newPoint);
      }
    });
    function formatSeriesData(datas: any) {
      return {
        name: series.type,
        data: datas
      };
    }
    seriesList.push(formatSeriesData(seriesData));
    downloadList.push(formatSeriesData(downloadData));
  });
  return { seriesList, downloadList };
}

/**
 * 获取chart的series (已分类-x轴为string类型)
 * @chartData 数据
 * @formartFun (item: any) => void 数据格式转换方法
 * @orderXFun (categories: any[]) => any[] 自定义排序方法
 * @check boolean 是否检查数据按x轴排序，无数据时补空点，保证每个序列长度、顺序一致
 */
export function stringTypeMap(chartData: any[], formatFun: (item: any) => any, orderXFun?: (categories: any[]) => any[], check?: boolean) {
  if (!chartData || chartData.length === 0) {
    return { seriesList: [], downloadList: [] };
  }
  const seriesList: any = [];
  const downloadList: any = [];

  let xCategories: any[] = [];
  let categories: any[] = [];
  chartData.map((series, index) => {
    let seriesData: any[] = [];
    let downloadData: any[] = [];
    series.data.forEach(point => {
      const formatItem = formatFun(point);
      const { x, y } = formatItem;
      downloadData.push({ x, y });
      if (x) {
        xCategories.push(x);
      }
      // 因为x只能是number，所以绘图时要去掉中文x
      let drawItem = commonUtils.deep(formatItem);
      drawItem.x = undefined;
      seriesData.push(drawItem);
    });

    function formatSeriesData(datas: any) {
      return {
        name: series.type,
        data: datas
      };
    }
    seriesList.push(formatSeriesData(seriesData));
    downloadList.push(formatSeriesData(downloadData));
  });
  // 去重，定义x轴，可选择是否给x轴排序
  categories = orderXFun ? orderXFun(commonUtils.uniqArray(xCategories)) : commonUtils.uniqArray(xCategories);

  if (orderXFun || check) {
    downloadList.map((item, index) => {
      // 检查数据按x轴排序，无数据时补空点，保证每个序列长度、顺序一致
      let orderTypelist: any[] = [];
      let orderDownloadTypeList: any[] = [];
      for (let i = 0; i < categories.length; i++) {
        const data = item.data.filter((element, key) => {
          if (element.x === categories[i]) {
            orderTypelist.push(seriesList[index].data[key]);
            orderDownloadTypeList.push(element);
            return true;
          }
          return false;
        });
        if (!data.length) {
          orderTypelist.push({});
        }
      }
      seriesList[index].data = orderTypelist;
      downloadList[index].data = orderDownloadTypeList;
    });
  }
  return { seriesList, downloadList, xList: categories };
}

// 未分类，x为数字或者日期
export function chartGroupBy(arr: any[], type: string, formatFun: (item: any) => any, xKey?: string) {
  if (!Array.isArray(arr) || typeof formatFun !== 'function') {
    return { seriesList: [], downloadList: [] };
  }
  // 分类
  const typeObj = arr.reduce(
    (result, item) => {
      const obj = result[0];
      const downloadObj = result[1];
      if (!obj[item[type]]) {
        obj[item[type]] = [];
        downloadObj[item[type]] = [];
      }
      obj[item[type]].push(formatFun(item));
      const { x, y } = formatFun(item);
      downloadObj[item[type]].push({ x: xKey ? item[xKey] : x, y });
      return [obj, downloadObj];
    },
    [{}, {}]
  );
  let list: any[] = []; // 绘图数据
  let downloadList: any[] = []; // 下载数据
  // tslint:disable-next-line:forin
  for (const key in typeObj[0]) {
    list.push({ name: key as string, data: typeObj[0][key] });
    downloadList.push({ name: key as string, data: typeObj[1][key] });
  }
  return { seriesList: list, downloadList };
}

// 未分类，x为string
export function stringGroupBy(arr: any[], type: string, formatFun: (item: any) => any, orderXFun?: (categories: any[]) => any[], check?: boolean) {
  if (!Array.isArray(arr) || typeof formatFun !== 'function') {
    return { seriesList: [], downloadList: [] };
  }
  let xCategories: any[] = [];
  // 分类
  const typeObj: any[] = arr.reduce(
    (result, item) => {
      const obj = result[0];
      const downloadObj = result[1];
      if (!obj[item[type]]) {
        obj[item[type]] = [];
        downloadObj[item[type]] = [];
      }
      const formatItem = formatFun(item);
      const { x, y } = formatItem;
      downloadObj[item[type]].push({ x, y });
      xCategories.push(x);
      // 因为x只能是number，所以绘图时要去掉中文x
      let drawItem = commonUtils.deep(formatItem);
      drawItem.x = undefined;
      obj[item[type]].push(drawItem);
      return [obj, downloadObj];
    },
    [{}, {}]
  );
  let list: any[] = [];
  let downloadList: any[] = [];
  // 去重，定义x轴，可选择是否给x轴排序
  let categories: any[] = orderXFun ? orderXFun(commonUtils.uniqArray(xCategories)) : commonUtils.uniqArray(xCategories);
  // tslint:disable-next-line:forin
  for (const key in typeObj[0]) {
    if (orderXFun || check) {
      // 检查数据按x轴排序，无数据时补空点，保证每个序列长度、顺序一致
      let orderTypelist: any[] = [];
      let orderDownloadTypeList: any[] = [];
      for (let i = 0; i < categories.length; i++) {
        const data = typeObj[1][key].filter((element, index) => {
          if (element.x === categories[i]) {
            orderTypelist.push(typeObj[0][key][index]);
            orderDownloadTypeList.push(element);
            return true;
          }
          return false;
        });
        if (!data.length) {
          orderTypelist.push({});
        }
      }
      list.push({ name: key as string, data: orderTypelist });
      downloadList.push({ name: key as string, data: orderDownloadTypeList });
    } else {
      // x轴顺序不需要调整，且接口里每个序列长度、顺序本身就是一致时
      list.push({ name: key as string, data: typeObj[0][key] });
      downloadList.push({ name: key as string, data: typeObj[1][key] });
    }
  }
  return { seriesList: list, downloadList, xList: categories };
}

/**
 * 对chart‘柱’的数据处理
 * @param data  点数据
 */
export function formatColumnData(data: any, obj?: any) {
  if (!data || data.length === 0) {
    return [];
  }
  let dataList: any[] = [];
  data.map((series, index) => {
    const item = {
      ...series,
      type: 'column',
      ...obj
    };
    dataList.push(item);
  });
  return dataList;
}

/**
 * 对chart‘线条’的数据处理
 * @param data  点数据
 */
export function formatSplineData(data: any, obj?: any, type?: string) {
  if (!data || data.length === 0) {
    return [];
  }
  const spLineColor = getSplineColor(type);
  let dataList: any[] = [];
  data.map((series, index) => {
    const item = {
      ...series,
      type: 'spline',
      lineWidth: 2,
      ...spLineColor, // 设置MA线条颜色
      ...obj
    };
    dataList.push(item);
  });
  return dataList;
}

/**
 * 对chart‘标记’的数据处理
 * @param data  点数据
 */
export function formatFlagData(obj?: any, color?: string) {
  let dataList: any[] = [];
  const item = {
    type: 'flags',
    data: [
      {
        x: 0,
        text: ''
      }
    ],
    cursor: 'pointer',
    style: {
      fontWeight: 'normal',
      textAlign: 'center',
      color: color ? color : '#fff'
    },
    fillColor: 'transparent',
    lineColor: '#4D4D57',
    lineWidth: 0.5,
    onSeries: 'candlestick', // 设置在蜡烛图上绘制标志
    shape: 'squarepin',
    useHTML: true,
    states: {
      hover: {
        enabled: false
      }
    },
    enableMouseTracking: false, // 禁用鼠标跟踪
    ...obj
  };
  dataList.push(item);
  return dataList;
}

/**
 * 对chart‘点’的数据处理
 * @param data  点数据
 */
export function formatPointData(data: any, obj?: any) {
  if (!data || data.length === 0) {
    return [];
  }
  let dataList: any[] = [];
  data.map((series, index) => {
    const item = {
      ...series,
      type: 'scatter',
      ...obj
    };
    dataList.push(item);
  });
  return dataList;
}

/**
 * 对chart‘线’的数据处理
 * @param data  线数据
 * @param obj 自定义其他属性
 */
export function formatLineData(data: any, obj?: any) {
  if (!data || data.length === 0) {
    return [];
  }
  let dataList: any[] = [];
  data.map((series, index) => {
    const item = {
      ...series,
      type: 'line',
      step: 'left',
      dashStyle: 'Solid',
      // stacking: series.stacking, // 显示是否堆积
      ...obj
    };
    dataList.push(item);
  });
  return dataList;
}

/**
 * 对chart‘域’的数据处理
 * @param data  域数据
 */
export function formatAreaData(data: any, obj?: any) {
  if (!data || data.length === 0) {
    return [];
  }
  let dataList: any[] = [];
  data.map((series, index) => {
    const item = {
      ...series,
      type: 'area',
      fillColor: {
        // 设置渐变透明度
        linearGradient: {
          x1: 0,
          y1: 0,
          x2: 0,
          y2: 1
        },
        stops: [[0, 'rgba(42,156,255,0.25) 0%'], [1, 'rgba(216,216,216,0.00) 100%']]
      },
      ...obj
    };
    dataList.push(item);
  });
  return dataList;
}

/**
 * 对chart‘烛台’的数据处理
 * @param data  域数据
 */
export function formatCandleStickData(data: any, obj?: any) {
  if (!data || data.length === 0) {
    return [];
  }
  let dataList: any[] = [];
  data.map((series, index) => {
    const item = {
      ...series,
      type: 'candlestick',
      color: '#2EB785',
      lineColor: '#2EB785',
      upColor: '#E0294A',
      upLineColor: '#E0294A',
      ...obj
    };
    dataList.push(item);
  });
  return dataList;
}

/**
 * 对chart‘饼图’的数据处理
 * @param data  点数据
 */
export function formatPieData(data: any, obj?: any) {
  if (!data || data.length === 0) {
    return [];
  }
  let dataList: any[] = [];
  data.map((series, index) => {
    const item = {
      ...series,
      type: 'pie',
      borderWidth: 0,
      dataLabels: {
        enabled: false
      },
      showInLegend: true,
      innerSize: '50%',
      turboThreshold: 10000,
      ...obj
    };
    dataList.push(item);
  });
  return dataList;
}

// x轴类型枚举
export const xType = {
  num: 'Number',
  str: 'String',
  date: 'Data'
};

// MA类型枚举
export const maType = {
  MA5: 'ma5',
  MA10: 'ma10',
  MA30: 'ma30',
  MA60: 'ma60'
};

export function formatXYData(point: any, xKey: string, yKey: string, xDataType?: string) {
  const x = xDataType === xType.date ? Number.parseFloat(moment(point[xKey]).format('x')) : point[xKey];
  const y = Number.parseFloat(String(point[yKey]));
  let obj = {};
  if (xDataType === xType.str) {
    obj = { y, tooltip: point };
  } else {
    obj = {
      x,
      y,
      tooltip: point
    };
  }
  return obj;
}

// 饼状图-数据格式需要{name, y}
export function formatYNameData(point: any, nameKey: string, yKey: string) {
  const name = point[nameKey];
  const y = Number.parseFloat(String(point[yKey]));
  let obj = {};
  obj = {
    name,
    y,
    x: name,
    tooltip: point
  };
  return obj;
}

/**
 * 返回成交量格式数据
 * @point 单条成交量数据
 * @xKey x轴Key
 * @yKey y轴key
 * @openKey 开盘价key
 * @closeKey 收盘价key
 * @xDataType x轴数据类型
 */
export function formatXYWithColorData(point: any, xKey: string, yKey: string, openKey: string, closeKey: string, xDataType?: string) {
  const x = xDataType === xType.date ? Number.parseFloat(moment(point[xKey]).format('x')) : point[xKey];
  const y = Number.parseFloat(String(point[yKey]));
  let obj = {};
  if (xDataType === xType.str) {
    obj = { y, tooltip: point };
  } else {
    // 设置成交量涨跌颜色
    const color = { color: point[openKey] >= point[closeKey] ? '#2EB785' : '#E0294A' };
    obj = {
      x,
      y,
      tooltip: point,
      ...color
    };
  }
  return obj;
}

/**
 * 返回蜡烛图格式数据
 * @point 单条蜡烛图数据
 * @xKey x轴Key
 * @openKey 开盘价key
 * @highKey 最高价key
 * @lowKey 最低价key
 * @closeKey 收盘价key
 * @xDataType x轴数据类型
 */
export function formatXYCandleStickData(
  point: any,
  xKey: string,
  openKey: string,
  highKey: string,
  lowKey: string,
  closeKey: string,
  xDataType?: string
) {
  const x = xDataType === xType.date ? Number.parseFloat(moment(point[xKey]).format('x')) : point[xKey];
  const open = Number.parseFloat(String(point[openKey]));
  const high = Number.parseFloat(String(point[highKey]));
  const low = Number.parseFloat(String(point[lowKey]));
  const close = Number.parseFloat(String(point[closeKey]));
  // 设置涨跌颜色
  const color = { color: open >= close ? '#2EB785' : '#E0294A' };
  const obj = {
    x,
    open,
    high,
    low,
    close,
    tooltip: point,
    ...color
  };
  return obj;
}

export function getSplineColor(type?: string) {
  let colorObj: any = null;
  // 根据传入MA的类型返回线条颜色
  switch (type) {
    case maType.MA5:
      colorObj = { color: chartColors.colors1[0] };
      break;
    case maType.MA10:
      colorObj = { color: chartColors.colors1[5] };
      break;
    case maType.MA30:
      colorObj = { color: chartColors.colors1[9] };
      break;
    case maType.MA60:
      colorObj = { color: chartColors.colors1[13] };
      break;
    default:
      return null;
  }
  return colorObj;
}

/**
 * 最小值、最大值动态计算方法
 * @minTime 时间范围最小值
 * @maxTime 时间范围最大值
 * @chartData 下载的绘图数据
 * @highChartSeries 最大值序列
 * @lowChartSeries 最小值序列
 * @timeString 时间字符名称
 * @orderHigh 最大值字符名称
 * @orderLow 最小值字符名称
 */
export function showFlags(updateFlagConfig: IUpdateFlagConfig) {
  const { minTime, maxTime, chartData, highChartSeries, lowChartSeries, timeString, orderHigh, orderLow } = updateFlagConfig;
  if (!chartData) {
    return;
  }
  var lowestPrice,
    highestPrice,
    array: any = [],
    highestArray = [],
    lowestArray = [],
    highestTime,
    lowestTime,
    flagsMaxData,
    flagsMinData;

  // 取得chart图显示区域内数据
  for (var i = 0; i < chartData.length; i++) {
    if (chartData[i][timeString] >= minTime && chartData[i][timeString] <= maxTime) {
      array.push({
        [timeString]: chartData[i][timeString],
        [orderHigh]: chartData[i][orderHigh], // 最高价
        [orderLow]: chartData[i][orderLow] // 最低价
      });
    }
  }
  if (array.length === 0) {
    return;
  }
  // 获取最高价和时间
  highestArray = commonUtils.orderBy(array, [orderHigh], ['desc'])[0]; // 根据最高价降序排列
  highestTime = highestArray[timeString];
  highestPrice = highestArray[orderHigh];
  // 获取最低价和时间
  lowestArray = commonUtils.orderBy(array, [orderLow], ['asc'])[0]; // 根据最低价升序排列
  lowestTime = lowestArray[timeString];
  lowestPrice = lowestArray[orderLow];
  flagsMaxData = [{ x: highestTime, title: span(commonUtils.formatContent(highestPrice, false, false, false, 2, 0, true)) }];
  flagsMinData = [{ x: lowestTime, title: span(commonUtils.formatContent(lowestPrice, false, false, false, 2, 0, true)) }];
  // 动态update flags(最高价)
  highChartSeries.update({ data: flagsMaxData });
  // 动态update flags(最低价)
  lowChartSeries.update({ data: flagsMinData });
}

// 标记显示特殊设置（解决标记显示不全）
function span(params: any) {
  return `
    <span style="display: inline-block;
      background-color: transparent;
      border: 1px solid #4D4D57;
      margin: -3px;
      padding: 6px;
    ">${params}</span>`;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ABSChart/utils.ts